import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Chrome, Mail, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import PublicNavbar from "../../components/PublicNavbar";
import CinematicLayout from "../../components/CinematicLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StaffLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [form, setForm] = useState({ email: "", password: "" });
  const [showAdminCode, setShowAdminCode] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/staff/me`, {
          credentials: "include"
        });
        if (response.ok) {
          const user = await response.json();
          if (user.status === "active") {
            navigate("/dashboard", { replace: true });
          } else if (user.status === "pending") {
            navigate("/staff/pending", { state: { user }, replace: true });
          }
        }
      } catch {
        // Expected when user is not authenticated - no action needed
      }
    };
    checkAuth();
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/staff/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form)
      });

      if (response.ok) {
        const user = await response.json();
        if (user.status === "active") {
          toast.success("Welcome back!");
          navigate("/dashboard");
        } else if (user.status === "pending") {
          toast.info("Account pending approval");
          navigate("/staff/pending", { state: { user } });
        } else if (user.status === "denied") {
          toast.error("Your account has been denied access");
        } else if (user.status === "disabled") {
          toast.error("Your account has been disabled");
        } else {
          toast.error(`Account status: ${user.status}`);
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || "Login failed. Please check your email and password.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const url = new URL(`${API}/auth/staff/google`);
    url.searchParams.set("origin_url", window.location.origin);
    if (adminCode?.trim()) {
      url.searchParams.set("admin_code", adminCode.trim());
    }
    window.location.href = url.toString();
  };

  return (
    <CinematicLayout showLogo>
      <PublicNavbar />

      <section className="relative z-10 pt-24 pb-12 px-6 flex items-center justify-center min-h-[calc(100vh-80px)]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="/logo-reflection-trimmed.png"
              alt="DopeInHD"
              className="h-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-[#FAFAFA] mb-2">Staff Portal</h1>
            <p className="text-[#A1A1AA]">Sign in to access the command center</p>
          </div>

          <div className="bg-[#121214]/90 backdrop-blur-sm border border-[#27272A] rounded-xl p-8">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <Label className="text-[#A1A1AA]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="you@example.com"
                    data-testid="staff-login-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#A1A1AA]">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
                  <Input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="pl-10 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    placeholder="••••••••"
                    data-testid="staff-login-password"
                  />
                </div>
              </div>

              <div className="flex justify-between text-xs">
                <Link to="/staff/forgot-username" className="text-[#E5C558] hover:text-[#FFD700]">
                  Forgot username?
                </Link>
                <Link to="/staff/forgot-password" className="text-[#E5C558] hover:text-[#FFD700]">
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37]"
                data-testid="staff-login-submit"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#27272A]"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-[#121214] px-2 text-[#71717A]">or continue with</span>
              </div>
            </div>

            <Button
              onClick={handleGoogleLogin}
              variant="outline"
              className="w-full border-[#27272A] text-[#FAFAFA] hover:bg-[#1A1A1D]"
              data-testid="staff-google-login"
            >
              <Chrome size={20} className="mr-2" />
              Sign in with Google
            </Button>

            {/* Admin Code Section */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowAdminCode(!showAdminCode)}
                className="text-xs text-[#71717A] hover:text-[#D4AF37] transition-colors"
              >
                {showAdminCode ? "Hide admin code" : "Have an admin code?"}
              </button>
              {showAdminCode && (
                <Input
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  placeholder="Enter admin invite code"
                  className="mt-2 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] text-sm"
                  data-testid="admin-code-input"
                />
              )}
            </div>

            <p className="mt-6 text-center text-sm text-[#A1A1AA]">
              Don&apos;t have an account?{" "}
              <Link to="/staff/signup" className="text-[#D4AF37] hover:underline">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </section>
    </CinematicLayout>
  );
};

export default StaffLogin;
